import { ArrowLink, CardCategory, Col } from "@/components";
import { Section, SectionHeading } from "@/components/ui/Section";
import clsx from "clsx";
import { omit } from "lodash";
import { ComponentType, ReactNode, useMemo } from "react";
import { RichText } from "../RichText";
import "./HomeServices.scss";

export const HOME_SERVICES_LAYOUTS = {
  LayoutOne: "LayoutOne",
  LayoutTwo: "LayoutTwo",
  LayoutThree: "LayoutThree",
  LayoutFour: "LayoutFour",
} as const;

type Layout =
  (typeof HOME_SERVICES_LAYOUTS)[keyof typeof HOME_SERVICES_LAYOUTS];

export type AggregationItemsProps = {
  title: ReactNode;
  description?: ReactNode;
  goToLabel?: string;
  href?: string;
  asLink?: "a" | ComponentType<any>;
  items: any[];
  layout?: Layout;
  className?: string;
  showBlueBackground?: boolean;
};

export const HomeServices = ({
  title,
  description,
  goToLabel,
  href,
  asLink = "a",
  items,
  layout = HOME_SERVICES_LAYOUTS.LayoutOne,
  className,
  showBlueBackground = false,
}: AggregationItemsProps) => {
  const layoutClassName =
    layout === HOME_SERVICES_LAYOUTS.LayoutOne
      ? "ui-home-services-layout-one"
      : layout === HOME_SERVICES_LAYOUTS.LayoutTwo
        ? "ui-home-services-layout-two"
        : layout === HOME_SERVICES_LAYOUTS.LayoutThree
          ? "ui-home-services-layout-three"
          : layout === HOME_SERVICES_LAYOUTS.LayoutFour
            ? "ui-home-services-layout-four"
            : "";

  const itemsNormalized = useMemo(() => {
    switch (layout) {
      case HOME_SERVICES_LAYOUTS.LayoutOne:
        return items.map((item, index) => {
          const removeImage = index !== 0;
          return {
            ...omit(item, removeImage ? ["image"] : []),
          };
        });
      case HOME_SERVICES_LAYOUTS.LayoutTwo:
        return items.map((item) => {
          const removeImage = false;
          return {
            ...omit(item, removeImage ? ["image"] : []),
          };
        });
      case HOME_SERVICES_LAYOUTS.LayoutThree:
        return items.map((item, index) => {
          const removeImage = index !== 0;
          return {
            ...omit(item, removeImage ? ["image"] : []),
          };
        });
      case HOME_SERVICES_LAYOUTS.LayoutFour:
        return items.map((item, index) => {
          const removeImage = index !== 2;
          return {
            ...omit(item, removeImage ? ["image"] : []),
          };
        });
      default:
        return items;
    }
  }, [items, layout]);

  return (
    <Section
      className={clsx(
        "base-col ui-home-services-section",
        className,
        showBlueBackground ? "blue-background" : "",
      )}
    >
      <Col className="ui-home-services-section-content">
        <SectionHeading
          title={title}
          description={<RichText description={description}></RichText>}
          className="ui-home-services-section-title"
          color="slate-20"
          mdFs="1"
          titleMarginBottom="mb-x0"
          descriptionMarginBottom="mb-x0"
        >
          {goToLabel && href && (
            <ArrowLink href={href} asLink={asLink} fs="7">
              {goToLabel.toUpperCase()}
            </ArrowLink>
          )}
        </SectionHeading>
      </Col>
      <ul
        role="list"
        aria-label={`Lista di ${title}`}
        className={`list-style-none ${layoutClassName}`}
      >
        {itemsNormalized?.map((item, index) => {
          return (
            <li key={[item._id, index].join("-")}>
              <CardCategory
                key={[item._id, index].join("-")}
                title={item?.name}
                description={item?.description}
                image={item?.image}
                href={item?.link || item?.externalLink}
                showIcon={item?.externalLink}
                asLink={asLink}
                isExternal={item?.externalLink}
              />
            </li>
          );
        })}
      </ul>
    </Section>
  );
};
